import { useField } from "formik";
import GoogleMapsLoader from "react-google-maps-loader";
import InputMask from "react-input-mask";
import PhoneInput from "react-phone-number-input/input";

import DatePicker from "react-datepicker";
import {
  MdKeyboardArrowUp,
  MdDateRange,
  MdKeyboardArrowDown,
  MdError,
  MdSearch,
} from "react-icons/md";
import { FaMobileAlt, FaPhone } from "react-icons/fa";
import ReactSelect from "./ReactSelect";

import { FaUniversity, FaCreditCard, FaRegMoneyBillAlt } from "react-icons/fa";
import GooglePlacesSuggest from "react-google-places-suggest";

import { ValidationError } from "./Validation";

import { displayAddress, saveGoogleAddress } from "../helpers/Address";
import { renderDate } from "../helpers/Date";
import { useCallback, useEffect, useMemo, useState } from "react";

import "../styles/components/_dropdowns.scss";

function Input({
  name,
  label,
  type = "text",
  required,
  startIcon,
  className,
  inputClassName = "",
  children,
  modified,
  onChange,
  ...rest
}) {
  const [field, meta, { setTouched }] = useField({ name, type });

  const handleChange = useCallback(
    (event) => {
      field.onChange(event);
      if (typeof onChange === "function") {
        onChange(event);
      }
    },
    [field, onChange]
  );

  const handleBlur = useCallback(
    (event) => {
      field.onBlur(event);
      setTouched(true);
    },
    [field, setTouched]
  );

  return (
    <div className={className}>
      {modified ? (
        <div className="label-with-meta">
          <label htmlFor={name}>{label}</label>
          <small className="meta">Updated: {renderDate(modified)}</small>
        </div>
      ) : (
        <label htmlFor={name}>{label}</label>
      )}
      <div className="relative">
        {startIcon && <span className="input-start-icon">{startIcon}</span>}
        <InputMask
          maskChar={" "}
          name={name}
          id={name}
          type={type}
          onBlur={handleBlur}
          className={`${inputClassName} ${
            startIcon ? "start_icon_padding" : ""
          }`}
          value={field.value}
          onChange={handleChange}
          {...rest}
        />
        {children}
        <MdError className="input__alert-icon" />
        <ValidationError
          messages={meta.error ? [meta.error] : undefined}
          show={!!meta.error && meta.touched}
        />
        <span className="input__focus" />
      </div>
    </div>
  );
}

export function Checkbox({ children, name }) {
  const [field, , helpers] = useField({ name, type: "checkbox" });

  const handleClick = useCallback(
    () => helpers.setValue(!field.checked),
    [field, helpers]
  );

  return (
    <label className={`relative checkbox`}>
      <input
        type="checkbox"
        name={name}
        checked={field.value}
        onClick={handleClick}
      />
      <span>{children}</span>
    </label>
  );
}

//  * Basic text field.
//  * @type {Object}
//  */
export function Text({ name, ...rest }) {
  return <Input name={name} {...rest} />;
}

export function Number({ className = "", ...rest }) {
  return (
    <Input type="number" className={`${className} form__password`} {...rest} />
  );
}

export function Password({ className = "", ...rest }) {
  return (
    <Input
      type="password"
      className={`${className} form__password`}
      {...rest}
    />
  );
}

export function Email({ className = "", ...rest }) {
  return (
    <Input type="email" className={`${className} form__email`} {...rest} />
  );
}

export function Currency({ step, name, label, className }) {
  return (
    <div className={`form__currency ${className}`}>
      <label htmlFor={name}>{label}</label>
      <div className="relative">
        <span className="form__amount--dollar">$</span>
        <Number step={step} name={name} />
      </div>
    </div>
  );
}

export function Date({ name, label, ...rest }) {
  const [field, meta, helpers] = useField({ name, type: "date" });

  const onChange = useCallback((val) => helpers.setValue(val), [helpers]);

  return (
    <div className="form__date">
      <label htmlFor={name}>{label}</label>
      <div className="input__icon--right relative inline-block">
        <DatePicker
          customInput={<ButtonDateInput />}
          {...field}
          onChange={onChange}
          selected={field.value}
          {...rest}
        />
        <MdDateRange className="position__center--right blue is-not-interactive" />
      </div>

      <MdError className="input__alert-icon" />
      <ValidationError
        messages={meta.error ? [meta.error] : undefined}
        show={!!meta.error && meta.touched}
      />

      <span className="input__focus" />
    </div>
  );
}
function ButtonDateInput({ onClick, value }) {
  return (
    <button className="datebutton-custom-input" onClick={onClick}>
      {value}
    </button>
  );
}

export function InternationalPhone({ placeholder, value, onChange, ...rest }) {
  return (
    <PhoneInput
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      {...rest}
    />
  );
}

export function InternationalPhoneFormik({
  label,
  name,
  placeholder = "+61 491 570 156",
  ...rest
}) {
  const [field, meta, helpers] = useField({ name });
  return (
    <div>
      {label && <label>{label}</label>}
      <>
        <InternationalPhone
          value={field.value || ""}
          onChange={helpers.setValue}
          onBlur={helpers.setTouched}
          autoComplete="off"
          placeholder={placeholder}
          international
          {...rest}
        />
        <ValidationError
          messages={meta.error ? [meta.error] : undefined}
          show={!!meta.error && meta.touched}
        />
      </>
    </div>
  );
}

export function Phone({ name, onChange, onBlur, ...rest }) {
  const handleBlur = useCallback(
    (event) => {
      let value = event.target.value;
      if (value && typeof value === "string") {
        value = value.replace(/[^0-9]/g, "");
        value =
          name === "mobile"
            ? `${value.slice(0, 4)} ${value.slice(4, 7)} ${value.slice(7, 15)}`
            : `${value.slice(0, 2)} ${value.slice(2, 6)} ${value.slice(
                6,
                10
              )} ${value.slice(10, 15)}`;
        value = value.trim();
      }

      if (onChange && typeof onChange === "function") {
        onChange(
          {
            ...event,
            type: "change",
            target: { ...event.target, value },
            _reactName: "onChange",
          },
          value
        );
      }
      if (onBlur && typeof onBlur === "function") {
        onBlur({
          ...event,
          target: { ...event.target, value },
        });
      }
    },
    [onChange, onBlur, name]
  );

  const onKeyDown = useCallback((event) => {
    const allowed = ["+", "*", "#", "Backspace", "Tab"];
    if (isNaN(event.key) && !allowed.includes(event.key)) {
      event.preventDefault();
    }
  }, []);

  return (
    <div className="relative">
      <span className="input-start-icon">
        {name === "mobile" ? <FaMobileAlt /> : <FaPhone />}
      </span>
      <input
        {...rest}
        name={name}
        onKeyDown={onKeyDown}
        placeholder={name === "mobile" ? "1234 567 890" : "04 1234 5678"}
        onBlur={handleBlur}
        onChange={onChange}
      />
    </div>
  );
}

export function PhoneFormikField({ name, label, ...rest }) {
  const [field, meta, { setValue, setTouched }] = useField({ name });
  const handleChange = useCallback(
    (e) => {
      setValue(e.target.value);
    },
    [setValue]
  );

  const handleBlur = useCallback(
    (e) => {
      setTouched(true);
      field.onBlur(e);
    },
    [setTouched, field]
  );
  // console.log(field);
  return (
    <div>
      {label && <label>{label}</label>}
      <Phone
        {...field}
        {...rest}
        name={name}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      <ValidationError
        messages={meta.error ? [meta.error] : undefined}
        show={!!meta.error && meta.touched}
      />
    </div>
  );
}

export function CardExpiryDate({ ...rest }) {
  return (
    <Input dateFormat="MM/YY" {...rest}>
      <MdDateRange className="icon icon--right position__center--right blue is-not-interactive" />
    </Input>
  );
}

export function Select({ name, label, options, onChange, className, ...rest }) {
  const [field, , helpers] = useField({ name });

  const renderDropdownArrow = ({ isOpen }) => {
    const MdArrow = isOpen ? MdKeyboardArrowUp : MdKeyboardArrowDown;
    const blueClass = isOpen ? "" : " blue";

    return (
      <MdArrow
        className={`Select__arrow position__center--right${blueClass}`}
      />
    );
  };

  const value = useMemo(
    () =>
      typeof field?.value === "string"
        ? options.find((option) => option.value === field.value) || {}
        : field.value,
    [options, field]
  );

  const handleChange = useCallback(
    (option) => {
      helpers.setValue(option?.label);
      if (typeof onChange === "function") {
        onChange(option);
      }
    },
    [helpers, onChange]
  );

  return (
    <div className={`form__select ${className}`}>
      <label htmlFor={name}>{label}</label>
      <ReactSelect
        value={value}
        onChange={handleChange}
        onBlur={field.onBlur}
        arrowRenderer={renderDropdownArrow}
        options={options}
        {...rest}
      />
    </div>
  );
}

export function SelectTitle({ name, otherDisabled, ...rest }) {
  const [field] = useField({ name });

  const display = [field.value, field.value?.value].includes("Other");

  const honorificOptions = [
    { value: "Mr", label: "Mr" },
    { value: "Mrs", label: "Mrs" },
    { value: "Miss", label: "Miss" },
    { value: "Ms", label: "Ms" },
    { value: "Other", label: "Other" },
  ];

  return (
    <div className="select-container flex-row form__row">
      <div>
        <Select
          name={name}
          label="Title"
          clearable={false}
          options={honorificOptions}
          {...rest}
        />
      </div>

      {display && (
        <Text
          label="Other"
          name="honorific_other"
          className="flex-row__stretch"
          disabled={otherDisabled}
        />
      )}
    </div>
  );
}

export function SelectPaymentFrequency(rest) {
  const paymentFrequencies = [
    { value: "Monthly", label: "Monthly" },
    { value: "Fortnightly", label: "Fortnightly" },
    { value: "Quarterly", label: "Quarterly" },
    { value: "Half-Yearly", label: "Half-Yearly" },
    { value: "Annually", label: "Annually" },
  ];

  return (
    <Select
      label="Frequency"
      {...rest}
      clearable={false}
      options={paymentFrequencies}
    />
  );
}

export function SelectPaymentMethod({ creditCard, directDebit, ...rest }) {
  const optionIcon = (value) => {
    switch (value) {
      case "Direct Debit":
        return FaUniversity;
      case "Credit Card":
        return FaCreditCard;
      case "Direct Deposit":
        return FaRegMoneyBillAlt;
      default:
        return FaRegMoneyBillAlt;
    }
  };

  const renderOptions = (option) => {
    const FaOptionIcon = optionIcon(option.value);
    return (
      <p>
        <FaOptionIcon className="Select__icon" />
        {option.label}
      </p>
    );
  };

  //     const {, ...selectProps } = this.props;
  const hasDirectDebit =
    typeof directDebit !== "undefined" &&
    Object.keys(directDebit).length > 0 &&
    directDebit.number_masked !== "";
  const hasCreditCard =
    typeof creditCard !== "undefined" &&
    Object.keys(creditCard).length > 0 &&
    creditCard.number !== "";
  const paymentMethods = [];

  // If the user has a direct debit, add it as a payment option.
  if (hasDirectDebit === true) {
    paymentMethods.push({ value: "Direct Debit", label: "Direct Debit" });
  }

  // If the user has a credit card, add it as a payment option.
  if (hasCreditCard === true) {
    paymentMethods.push({ value: "Credit Card", label: "Credit Card" });
  }

  // Always add direct deposit as as the last payment option
  paymentMethods.push({ value: "Direct Deposit", label: "Direct Deposit" });

  return (
    <Select
      label="Payment Method"
      clearable={false}
      options={paymentMethods}
      optionRenderer={renderOptions}
      valueRenderer={renderOptions}
      {...rest}
    />
  );
}

export function AddressSuggest({
  name,
  label,
  googleMaps,
  textNoResults = "No Results Found",
  onSelectSuggest,
  ...rest
}) {
  const [{ value, onBlur }, meta, { setValue }] = useField({ name });

  const [bounds, setBounds] = useState(
    new window.google.maps.LatLngBounds(
      new window.google.maps.LatLng(-90, -180),
      new window.google.maps.LatLng(90, 180)
    )
  );

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(function (position) {
        const geolocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        const circle = new window.google.maps.Circle({
          center: geolocation,
          radius: position.coords.accuracy,
        });
        setBounds(circle.getBounds());
      });
    }
  }, []);

  const handleChangeInput = useCallback(
    (event) => {
      setValue(event.target.value);
    },
    [setValue]
  );

  const handleSelectSuggest = useCallback(
    (suggest) => {
      // Manipulate the Google address components into a format that suits our database
      const googleAddress = saveGoogleAddress(suggest.address_components);
      // Take the Google address and store it in a format that suits our form input
      const val = displayAddress(googleAddress);
      setValue(val);

      if (typeof onSelectSuggest === "function") {
        onSelectSuggest(val);
      }
    },
    [setValue, onSelectSuggest]
  );

  return (
    <GooglePlacesSuggest
      autocompletionRequest={{
        input: value,
        bounds,
        componentRestrictions: {
          country: ["au"],
        },
        types: ["street_address"],
      }}
      googleMaps={googleMaps}
      onSelectSuggest={handleSelectSuggest}
      bounds={bounds}
      textNoResults={textNoResults}
    >
      <div className="form__address input__icon--right">
        <label htmlFor={name}>{label}</label>
        <div className="relative">
          <input
            // {...this.getInputProps()}
            // className={inputClassName}
            name={name}
            type="text"
            value={value?.display || value}
            placeholder="Search a location"
            onChange={handleChangeInput}
            onBlur={onBlur}
            autoComplete="off"
          />

          <MdError className="input__alert-icon" />
          <ValidationError
            messages={meta.error ? [meta.error] : undefined}
            show={!!meta.error && meta.touched}
          />
          <span className="input__focus" />
          <MdSearch className="position__center--right" />
        </div>
      </div>
    </GooglePlacesSuggest>
  );
}

export const Address = ({
  key = "AIzaSyAubC93k4zSylXwyxx_HbiZ1ylfwMbxEE4",
  ...remainingProps
}) => (
  <GoogleMapsLoader
    render={(googleMaps) =>
      googleMaps && (
        <AddressSuggest googleMaps={googleMaps} {...remainingProps} />
      )
    }
    params={{
      key, // Define your api key here
      libraries: "places", // To request multiple libraries, separate them with a comma
    }}
  />
);

export const PasswordStrength = () => (
  <div className="password-strength-display">
    <strong>Password Strength : </strong>
    <p>
      Use at least 8 characters. Use at least one uppercase character. Use at
      least one lower character. Use at least one digit. Use at least one
      special character. Don&apos;t use a password from another site, or
      something too obvious like your pet&apos;s name.
    </p>
  </div>
);
